import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useState } from "react";
import { ClientRowObj } from "..";
import Searchbox from "components/fields/Searchbox";
import Pagination from "components/pagination";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import moment from "moment";
import { getImage } from "utils/commonFunction";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { uploadKycDocument } from "api/graphQL_customers";
import visa from "assets/svg/card_type/visa.svg";
import MasterCard from "assets/svg/card_type/mastercard.svg";
import Discover from "assets/svg/card_type/discover.svg";
import Amex from "assets/svg/card_type/amex.svg";
import MaestroCard from "assets/svg/card_type/maestro.svg";
import DinersClub from "assets/svg/card_type/dinersclub.svg";
import JCB from "assets/svg/card_type/jcb.svg";
import UnionPay from "assets/svg/card_type/unionpay.svg";
import { convertToFloat } from "utils/formatNumber";
import StatusRender from "views/admin/payments/components/StatusRender";
import AdvancedInfoModal from "views/admin/payments/components/AdvancedInfoModalNew";
import CustomToolTip from "components/tooltips";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { FiCheckCircle, FiEye, FiUpload } from "react-icons/fi";
import toast from "react-hot-toast";
import {
  Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow,
} from "@chakra-ui/react";
import Card from "components/card";
import FilterModal from "./FilterModal";
import { getAllClients } from "api/clients";

const columnHelper = createColumnHelper<ClientRowObj>();

function CustomerTable(props: {
  tableData: any;
  fetchClients: () => void;
  isLoading: boolean;
  roleData?: any;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  allGateways: any;
  onValueChange: (value: string) => void;
  allOrgDetails: any;
  filter: any; 
  setFilter: (value: any) => void;
  setClearClick: () => void;
  filterData: () => void;
}) {
  const {
    tableData, fetchClients, roleData, page, setPage, currentPage,
    totalpage, totalItems, pageSize, setPageSize, allGateways, onValueChange, isLoading,allOrgDetails,setFilter,filter,setClearClick,filterData
  } = props;

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState<any[]>([]);
  const [searchVal, setSearchVal] = useState<any>("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<any[]>([]);
  const [activeGatewayTransactions, setActiveGatewayTransactions] = useState("");
  const [activePaymentMethod, setActivePaymentMethod] = useState("");
  const [uploadingKYC, setUploadingKYC] = useState("");

  const [subClientsData, setSubClientsData] = useState([]);

  const resetRow = () => {
    setExpandedRow(null);
  }

  React.useEffect(() => { setExpandedRow(null); }, [page, pageSize]);

  useEffect(() => {
    getAllGatewaysSecond("").then((d) => setPaymentData(d ?? [])).catch(() => {});

    const processed = tableData.map((item: any) => {
      let transactions: any = item.transactionsList;
      try {
        if (Array.isArray(item.transactionsList)) {
          const parsed = item.transactionsList.map((t: string) => JSON.parse(t));
          transactions = parsed.reduce((acc: any, txn: any) => {
            if (!txn.gateway_id) return acc;
            if (!acc[txn.gateway_id]) acc[txn.gateway_id] = [];
            acc[txn.gateway_id].push(txn);
            return acc;
          }, {});
        }
      } catch {}

      let payment_methods: any = item.paymentMethods;
      try {
        if (Array.isArray(item.paymentMethods)) {
          const parsed = item.paymentMethods.map((p: string) => JSON.parse(p));
          payment_methods = parsed.reduce((acc: any, pm: any) => {
            if (!pm.type) return acc;
            if (!acc[pm.type]) acc[pm.type] = [];
            acc[pm.type].push(pm);
            return acc;
          }, {});
        }
      } catch {}

      let kyc: any = item.kyc;
      try { if (Array.isArray(item.kyc)) kyc = item.kyc.map((k: string) => JSON.parse(k)); } catch {}

      return { ...item, transactionsList: transactions, paymentMethods: payment_methods, kyc };
    });
    setData(processed);
  }, [tableData]);

  const getSubClientsData = () => {
      getAllClients(0)
      .then((data) => setSubClientsData(data[0]))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching clients"))
      .finally(() => {  });
  };
  
  React.useEffect(() => { getSubClientsData(); }, []);

  const handleValueChange = (e: any) => { setExpandedRow(null); onValueChange(e); setSearchVal(e); };
  const handleExpand = (rowId: any) => setExpandedRow(expandedRow === rowId ? null : rowId);
  const getGatewayName = (id: any) => paymentData.find((g) => g.id === id)?.name ?? "Unknown";
  const openGatewayTransactions = (val: any) => setActiveGatewayTransactions(activeGatewayTransactions === val ? "" : val);
  const openPaymentMethods = (val: any) => setActivePaymentMethod(activePaymentMethod === val ? "" : val);

  const getCardSVG = (type: string) => {
    const map: any = { VISA: visa, MasterCard, Discover, Amex, MaestroCard, DinersClub, JCB, UnionPay };
    return map[type] ? <img style={{ maxWidth: "65px" }} src={map[type]} alt={type} title={type} /> : "-";
  };

  const cardPatterns: Record<string, RegExp> = {
    VISA: /^4[0-9]{12}(?:[0-9]{3})?$/,
    MasterCard: /^5[1-5][0-9]{14}$|^2[2-7][0-9]{14}$/,
    Discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    Amex: /^3[47][0-9]{13}$/,
    MaestroCard: /^(5018|5020|5038|5893|6304|6759|6761|6762|6763)[0-9]{8,15}$/,
    DinersClub: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    JCB: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/,
    UnionPay: /^62\d{12,19}$/,
  };

  const getCardType = (num: string) => {
    const cleaned = num.replace(/\D/g, "");
    for (const [type, pattern] of Object.entries(cardPatterns)) {
      if (pattern.test(cleaned)) return type;
    }
    return "Unknown";
  };

  const handleFileChange = async (event: any, kycId: any, customerId: any) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpg", "image/jpeg", "image/svg", "image/svg+xml"];
    if (!allowed.includes(file.type)) { toast.error("Only PNG and SVG files are allowed."); return; }
    setUploadingKYC(kycId);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      await uploadKycDocument({ customer_id: customerId, kyc_id: kycId, document: reader.result as string })
        .then((d: any) => { setUploadingKYC(""); toast.success(d.message ?? "Document Uploaded"); fetchClients(); })
        .catch(() => setUploadingKYC(""));
    };
  };

  const columns = [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="clt-th">Created Date</span>,
      cell: (info: any) => (
        <div
          className="clt-expand-cell"
          onClick={() => handleExpand(info?.row?.original?.customer_id)}
        >
          {expandedRow === info?.row?.original?.customer_id
            ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
            : <AiFillPlusCircle className="clt-expand-icon" />}
          <span>{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      ),
    }),
    columnHelper.accessor("customer_id", {
      id: "customer_id",
      header: () => <span className="clt-th">Customer ID</span>,
      cell: (info: any) => <div className="clt-mono"><ShortTruncateCopy info={info?.getValue()} showCopy={true} /></div>,
    }),
    columnHelper.accessor("first_name", {
      id: "first_name",
      header: () => <span className="clt-th">Name</span>,
      cell: (info: any) => (
        <div className="clt-name-cell">
          <div className="clt-avatar">
            {info?.row?.original?.first_name?.charAt(0)?.toUpperCase() || "C"}
          </div>
          <span className="clt-name">{info?.row?.original?.first_name} {info?.row?.original?.last_name}</span>
        </div>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span className="clt-th">Email</span>,
      cell: (info) => <a href={`mailto:${info.getValue()}`} className="clt-link">{info.getValue()}</a>,
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => <span className="clt-th">Phone</span>,
      cell: (info) => <a href={`tel:${info.getValue()}`} className="clt-phone">{info.getValue()}</a>,
    }),
    columnHelper.accessor("source_client", {
      id: "source_client",
      header: () => <span className="clt-th">Source Client</span>,
      cell: (info: any) => <span className="clt-source">{info?.getValue()}</span>,
    }),
    columnHelper.accessor("total_transactions", {
      id: "total_transactions",
      header: () => <span className="clt-th">Transactions</span>,
      cell: (info: any) => (
        <div className="clt-txn-pill">
          <div className="clt-txn-dot" />
          <span>{info?.getValue() ?? 0}</span>
        </div>
      ),
    }),
  ];
  
  const table = useReactTable({
    data,
    columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  /* ── Expanded row sections ── */
  const renderExpanded = (row: any) => (
    <tr className="clt-expanded-row">
      <td colSpan={columns.length} className="clt-expanded-td">
        <div className="clt-expanded-inner">

          {/* Source Client */}
          <div className="clt-section">
            <div className="clt-section-header">
              <div className="clt-section-icon clt-section-icon--blue">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              </div>
              <h4 className="clt-section-title">Source Client</h4>
            </div>
            <div className="clt-inner-table-wrap">
              <table className="clt-inner-table">
                <thead><tr>
                  <th className="clt-inner-th">Client</th>
                  <th className="clt-inner-th">Reference ID</th>
                </tr></thead>
                <tbody><tr className="clt-inner-tr">
                  <td className="clt-inner-td">{row.original.source_client}</td>
                  <td className="clt-inner-td">{row.original.reference_id ?? "-"}</td>
                </tr></tbody>
              </table>
            </div>
          </div>

          {/* Gateways */}
          <div className="clt-section">
            <div className="clt-section-header">
              <div className="clt-section-icon clt-section-icon--amber">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <h4 className="clt-section-title">Gateways</h4>
            </div>
            {Object.keys(row.original.transactionsList).length > 0 ? (
              <div className="clt-inner-table-wrap">
                <table className="clt-inner-table">
                  <thead><tr>
                    <th className="clt-inner-th">Gateway {roleData?.[0]?.customers?.value?.show_customer_gateway_name ? "Name" : "Hash ID"}</th>
                    <th className="clt-inner-th">Customer ID</th>
                    <th className="clt-inner-th">Transactions</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(row.original.transactionsList).map(([gatewayId, transactions]: [string, any]) => (
                      <React.Fragment key={gatewayId}>
                        <tr className="clt-inner-tr">
                          <td className="clt-inner-td">
                            <div className="clt-expand-cell clt-expand-cell--sm" onClick={() => openGatewayTransactions(gatewayId + ":" + row.original.customer_id)}>
                              {activeGatewayTransactions === gatewayId + ":" + row.original.customer_id
                                ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
                                : <AiFillPlusCircle className="clt-expand-icon" />}
                              {roleData?.[0]?.customers?.value?.show_customer_gateway_name ? getGatewayName(gatewayId) : gatewayId}
                            </div>
                          </td>
                          <td className="clt-inner-td">-</td>
                          <td className="clt-inner-td">
                            <div className="clt-txn-pill clt-txn-pill--sm">{transactions.length}</div>
                          </td>
                        </tr>
                        {activeGatewayTransactions === gatewayId + ":" + row.original.customer_id && (
                          <tr>
                            <td colSpan={3} className="clt-inner-td-nested">
                              <table className="clt-inner-table">
                                <thead><tr>
                                  {["Date","Amount","Transaction ID","Internal ID","Gateway","Descriptor","Card Type","Status",
                                      ...((roleData[0]?.customers?.value?.view_customer_payment_response_information || roleData[0]?.customers?.value?.view_customer_gateway_response_information) ? ["Action"] : [])
                                    ].map((h) => (
                                    <th key={h} className="clt-inner-th">{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {transactions.map((txn: any, idx: any) => (
                                    <tr key={idx} className="clt-inner-tr">
                                      <td className="clt-inner-td clt-nowrap">{moment(txn.created_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                                      <td className="clt-inner-td clt-nowrap clt-upper">
                                        {convertToFloat(txn.amount)} {txn.currency}
                                        {txn.converted_amount && txn.converted_currency && <span> ({txn.converted_amount} {txn.converted_currency})</span>}
                                      </td>
                                      <td className="clt-inner-td"><ShortTruncateCopy info={txn?.et_id} showCopy={true} /></td>
                                      <td className="clt-inner-td"><ShortTruncateCopy info={txn?.id} showCopy={true} /></td>
                                      <td className="clt-inner-td">
                                        {!roleData[0]?.customers?.value?.show_customer_gateway_name
                                          ? <ShortTruncateCopy info={txn.gateway_id} showCopy={true} />
                                          : <span className="clt-gw-name">
                                              <img style={{ height: "auto", width: "15px" }} src={getImage(getGatewayName(txn.gateway_id))} alt="" />
                                              <p>{txn.gateway_id ? getGatewayName(txn.gateway_id) : "-"}</p>
                                            </span>}
                                      </td>
                                      <td className="clt-inner-td">{txn?.meta_info?.Descriptor}</td>
                                      <td className="clt-inner-td">{txn?.meta_info?.binInformation ? getCardSVG(txn?.meta_info?.binInformation?.card_type) : "-"}</td>
                                      <td className="clt-inner-td">
                                        <CustomToolTip
                                          content={txn?.payment_history?.length > 0
                                            ? txn.payment_history.map((h: any, i: number) => (
                                              <Card className="m-2 shadow" key={i}>
                                                <div className="border max-w-[70vh] border-white p-3">
                                                  {i === 0 && <p>Created at {h?.created_at} by API call from [{h?.client} - {h?.ip}] with status {h?.status}</p>}
                                                  {i !== 0 && <p>Status change from {h?.previous_status} to {h?.status} at {h?.status_changed_date}</p>}
                                                </div>
                                              </Card>
                                            ))
                                            : <span>No history available.</span>}
                                          trigger={<button className="flex items-center"><StatusRender status={txn?.status} value={txn?.status} /></button>}
                                          header="Status History"
                                        />
                                      </td>
                                      {(roleData[0]?.customers?.value?.view_customer_payment_response_information || roleData[0]?.customers?.value?.view_customer_gateway_response_information) && (
                                        <td className="clt-inner-td">
                                          <AdvancedInfoModal info={txn} client_is_deleted={false} />
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="clt-empty-section">No transactions available.</div>
            )}
          </div>

          {/* Payment Methods */}
          <div className="clt-section">
            <div className="clt-section-header">
              <div className="clt-section-icon clt-section-icon--green">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <h4 className="clt-section-title">Payment Methods</h4>
            </div>
            {Object.keys(row.original.paymentMethods).length > 0 ? (
              <div className="clt-inner-table-wrap">
                <table className="clt-inner-table">
                  <thead><tr>
                    <th className="clt-inner-th">Type</th>
                    <th className="clt-inner-th">Count</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(row.original.paymentMethods).map(([methodId, methods]: [string, any]) => (
                      <React.Fragment key={methodId}>
                        <tr className="clt-inner-tr">
                          <td className="clt-inner-td">
                            <div className="clt-expand-cell clt-expand-cell--sm" onClick={() => openPaymentMethods(methodId + ":" + row.original.customer_id)}>
                              {activePaymentMethod === methodId + ":" + row.original.customer_id
                                ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
                                : <AiFillPlusCircle className="clt-expand-icon" />}
                              <span className="clt-pm-type-badge">{methodId}</span>
                            </div>
                          </td>
                          <td className="clt-inner-td"><div className="clt-txn-pill clt-txn-pill--sm">{methods.length}</div></td>
                        </tr>
                        {activePaymentMethod === methodId + ":" + row.original.customer_id && (
                          <tr>
                            <td colSpan={3} className="clt-inner-td-nested">
                              <table className="clt-inner-table">
                                <thead><tr>
                                  <th className="clt-inner-th">Created Date</th>
                                  <th className="clt-inner-th">Payment Method ID</th>
                                  {methodId === "card" && <><th className="clt-inner-th">Last 4</th><th className="clt-inner-th">Card Type</th></>}
                                  {methodId === "bank" && <><th className="clt-inner-th">IBAN</th><th className="clt-inner-th">Swift</th><th className="clt-inner-th">Bank Name</th><th className="clt-inner-th">Account Number</th></>}
                                  {methodId === "crypto" && <th className="clt-inner-th">Wallet Address</th>}
                                </tr></thead>
                                <tbody>
                                  {methods.map((m: any, idx: any) => (
                                    <tr key={idx} className="clt-inner-tr">
                                      <td className="clt-inner-td clt-nowrap">{m.created_at ? moment(m.created_at).format("YYYY-MM-DD HH:mm:ss") : "-"}</td>
                                      <td className="clt-inner-td"><ShortTruncateCopy info={m.payment_method_id} showCopy={true} /></td>
                                      {methodId === "card" && <><td className="clt-inner-td">{m.card_number?.slice(-4)}</td><td className="clt-inner-td">{getCardSVG(getCardType(m.card_number))}</td></>}
                                      {methodId === "bank" && <><td className="clt-inner-td">{m.iban || "-"}</td><td className="clt-inner-td">{m.swift_no || "-"}</td><td className="clt-inner-td">{m.bank_name || "-"}</td><td className="clt-inner-td">{m.account_number || "-"}</td></>}
                                      {methodId === "crypto" && <td className="clt-inner-td"><ShortTruncateCopy info={m.wallet_address} showCopy={true} /></td>}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="clt-empty-section">No payment methods available.</div>
            )}
          </div>

          {/* KYC */}
          <div className="clt-section">
            <div className="clt-section-header">
              <div className="clt-section-icon clt-section-icon--purple">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </div>
              <h4 className="clt-section-title">KYC</h4>
            </div>
            {Object.keys(row.original.kyc).length > 0 ? (
              <div className="clt-inner-table-wrap">
                <table className="clt-inner-table">
                  <thead><tr>
                    <th className="clt-inner-th">Created Date</th>
                    <th className="clt-inner-th">KYC ID</th>
                    <th className="clt-inner-th">ID Number</th>
                    <th className="clt-inner-th">ID Type</th>
                    <th className="clt-inner-th">Issued At</th>
                    <th className="clt-inner-th">Expired At</th>
                    <th className="clt-inner-th">Status</th>
                    {(roleData?.[0]?.customers?.value?.upload_kyc_document || roleData?.[0]?.customers?.value?.view_kyc_document) && (
                      <th className="clt-inner-th">Actions</th>
                    )}
                  </tr></thead>
                  <tbody>
                    {Object.entries(row.original.kyc).map(([key, kyc_data]: [string, any]) => (
                      <tr key={key} className="clt-inner-tr">
                        <td className="clt-inner-td clt-nowrap">{kyc_data.created_date ? moment(kyc_data.created_date).format("YYYY-MM-DD HH:mm:ss") : "-"}</td>
                        <td className="clt-inner-td"><ShortTruncateCopy info={kyc_data.kyc_id} showCopy={true} /></td>
                        <td className="clt-inner-td clt-upper">{kyc_data.identification_number}</td>
                        <td className="clt-inner-td clt-upper">{kyc_data.identification_type}</td>
                        <td className="clt-inner-td">{kyc_data.document_issued_at || "-"}</td>
                        <td className="clt-inner-td">{kyc_data.document_expired_at || "-"}</td>
                        <td className="clt-inner-td">
                          {kyc_data.status === "verified"
                            ? <span className="clt-kyc-status clt-kyc-status--verified">Verified</span>
                            : <span className="clt-kyc-status clt-kyc-status--unverified">Unverified</span>}
                        </td>
                        {(roleData?.[0]?.customers?.value?.upload_kyc_document || roleData?.[0]?.customers?.value?.view_kyc_document) && (
                          <td className="clt-inner-td">
                            <div className="clt-kyc-actions">
                              {roleData?.[0]?.customers?.value?.upload_kyc_document && (
                                !kyc_data.document ? (
                                  uploadingKYC === kyc_data.kyc_id ? (
                                    <div className="clt-kyc-spinner" />
                                  ) : (
                                    <Popover trigger="hover" placement="bottom">
                                      <PopoverTrigger>
                                        <label htmlFor={`file-${key}`} className="cursor-pointer">
                                          <input type="file" accept="image/*,application/pdf" id={`file-${key}`} style={{ display: "none" }}
                                            onChange={e => { e.currentTarget.blur(); handleFileChange(e, kyc_data.kyc_id, row.original.customer_id); }} />
                                          <div className="clt-kyc-action-btn clt-kyc-action-btn--upload"><FiUpload size={14} /></div>
                                        </label>
                                      </PopoverTrigger>
                                      <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="180px" p="0" overflow="hidden" zIndex="20">
                                        <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">Upload Document</PopoverHeader>
                                        <PopoverArrow bg="#eff6ff" />
                                      </PopoverContent>
                                    </Popover>
                                  )
                                ) : (
                                  <Popover trigger="hover" placement="bottom">
                                    <PopoverTrigger>
                                      <div className="clt-kyc-action-btn clt-kyc-action-btn--done"><FiCheckCircle size={14} /></div>
                                    </PopoverTrigger>
                                    <PopoverContent border="1px solid #bbf7d0" borderRadius="12px" bg="#f0fdf4" maxW="180px" p="0" overflow="hidden" zIndex="20">
                                      <PopoverHeader bg="#dcfce7" fontSize="12px" fontWeight="700" color="#15803d" borderBottom="1px solid #bbf7d0" px="12px" py="9px">Document Uploaded</PopoverHeader>
                                      <PopoverArrow bg="#f0fdf4" />
                                    </PopoverContent>
                                  </Popover>
                                )
                              )}
                              {roleData?.[0]?.customers?.value?.view_kyc_document && kyc_data.status === "verified" && kyc_data.document && (
                                <Popover trigger="hover" placement="bottom">
                                  <PopoverTrigger>
                                    <button className="clt-kyc-action-btn clt-kyc-action-btn--view" onClick={() => {
                                      const [meta, b64] = kyc_data.document.split(",");
                                      const mime = meta.match(/data:(.*);base64/)[1];
                                      const bytes = atob(b64);
                                      const arr = new Uint8Array(bytes.length);
                                      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                                      const url = URL.createObjectURL(new Blob([arr], { type: mime }));
                                      window.open(url, "_blank");
                                    }}>
                                      <FiEye size={14} />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="180px" p="0" overflow="hidden" zIndex="20">
                                    <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View Document</PopoverHeader>
                                    <PopoverArrow bg="#eff6ff" />
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="clt-empty-section">No KYC data available.</div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="clt-card">
      {/* Header */}
      <div className="clt-header">
        <div className="clt-header-left">
          <div className="clt-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h3 className="clt-title">All Customers</h3>
            <p className="clt-subtitle">{totalItems > 0 ? `${totalItems} total customer${totalItems !== 1 ? "s" : ""}` : "No customers found"}</p>
          </div>
        </div>
        <div className="clt-search-wrap">
          <FilterModal
            subClientsData={subClientsData} setPage={props.setPage}
            filter={filter} setFilter={setFilter} roleData={roleData}
            setClearClick={setClearClick} 
            fetchAllsettlement={fetchClients}
            allOrgDetails={allOrgDetails}
            filterData={filterData}
            resetRow= {resetRow}
          />
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="clt-table-wrap">
        {isLoading ? (
          <div className="clt-loading">
            <div className="clt-spinner" />
            <p className="clt-loading-txt">Loading customers…</p>
          </div>
        ) : (
          <table className="clt-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan} onClick={h.column.getToggleSortingHandler()} className="clt-thead-th">
                      <div className="clt-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="clt-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <React.Fragment key={row.id}>
                    <tr className={`clt-tbody-tr${ri % 2 !== 0 ? " clt-tbody-tr--alt" : ""}${expandedRow === row.original.customer_id ? " clt-tbody-tr--expanded" : ""}`}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="clt-tbody-td">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {expandedRow === row.original.customer_id && renderExpanded(row)}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="clt-empty">
                      <div className="clt-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        </svg>
                      </div>
                      <p className="clt-empty-title">No customers found</p>
                      <p className="clt-empty-sub">Try adjusting your search or add a new customer</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="clt-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .clt-card { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; overflow: hidden; box-shadow: 0 2px 8px rgba(15,23,42,.06); }

        .clt-header { padding: 20px 24px 16px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
        .clt-header-left { display: flex; align-items: center; gap: 12px; }
        .clt-header-icon { width: 40px; height: 40px; border-radius: 11px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; display: flex; align-items: center; justify-content: center; color: #16a34a; flex-shrink: 0; }
        .clt-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -.2px; margin-bottom: 2px; line-height: 22px; }
        .clt-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .clt-search-wrap { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

        .clt-table-wrap { overflow-x: auto; }
        .clt-table-wrap::-webkit-scrollbar { height: 4px; }
        .clt-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .clt-table { width: 100%; border-collapse: collapse; }

        .clt-thead-th { padding: 12px 16px 10px; background: #f8fafc; border-bottom: 1.5px solid #e2e8f0; text-align: left; cursor: pointer; white-space: nowrap; user-select: none; }
        .clt-thead-th:hover { background: #f1f5f9; }
        .clt-th-inner { display: flex; align-items: center; gap: 5px; }
        .clt-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .5px; }
        .clt-sort-icon { color: #94a3b8; display: flex; align-items: center; }
        .clt-thead-th:hover .clt-sort-icon { color: #16a34a; }

        .clt-tbody-tr { transition: background .12s; }
        .clt-tbody-tr:hover { background: #f8fafc; }
        .clt-tbody-tr--alt { background: #fafbfc; }
        .clt-tbody-tr--alt:hover { background: #f1f5f9; }
        .clt-tbody-tr--expanded { background: #f0fdf4 !important; }
        .clt-tbody-td { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Cells */
        .clt-expand-cell { display: flex; align-items: center; gap: 7px; cursor: pointer; font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }
        .clt-expand-cell--sm { gap: 6px; font-size: 12.5px; }
        .clt-expand-icon { width: 18px; height: 18px; color: #94a3b8; flex-shrink: 0; transition: color .15s; }
        .clt-expand-icon--open { color: #16a34a; }
        .clt-expand-cell:hover .clt-expand-icon { color: #16a34a; }

        .clt-name-cell { display: flex; align-items: center; gap: 9px; }
        .clt-avatar { width: 30px; height: 30px; border-radius: 8px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #15803d; flex-shrink: 0; }
        .clt-name { font-size: 13px; font-weight: 600; color: #0f172a; }
        .clt-link { font-size: 13px; font-weight: 500; color: #2563eb; text-decoration: none; }
        .clt-link:hover { text-decoration: underline; }
        .clt-phone { font-size: 13px; font-weight: 500; color: #0f172a; text-decoration: none; }
        .clt-source { font-size: 13px; font-weight: 500; color: #0f172a; }
        .clt-mono { font-size: 12.5px; font-weight: 700; }

        .clt-txn-pill { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 7px; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #1d4ed8; font-size: 12.5px; font-weight: 700; }
        .clt-txn-pill--sm { padding: 3px 8px; font-size: 12px; }
        .clt-txn-dot { width: 6px; height: 6px; border-radius: 50%; background: #2563eb; }

        /* Expanded row */
        .clt-expanded-row { background: #f8fafc; }
        .clt-expanded-td { padding: 0; border-bottom: 2px solid #e2e8f0; }
        .clt-expanded-inner { padding: 20px 24px; display: flex; flex-direction: column; gap: 20px; }

        /* Sections inside expanded */
        .clt-section { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
        .clt-section-header { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #f8fafc; border-bottom: 1px solid #f1f5f9; }
        .clt-section-icon { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .clt-section-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .clt-section-icon--amber  { background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #d97706; }
        .clt-section-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .clt-section-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .clt-section-title { font-size: 13px; font-weight: 700; color: #0f172a; }
        .clt-empty-section { padding: 20px; font-size: 13px; color: #94a3b8; font-weight: 500; text-align: center; font-style: italic; }

        /* Inner tables */
        .clt-inner-table-wrap { overflow-x: auto; }
        .clt-inner-table { width: 100%; border-collapse: collapse; }
        .clt-inner-th { text-align: left;padding: 10px 14px 8px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .5px; white-space: nowrap; }
        .clt-inner-tr { transition: background .12s; }
        .clt-inner-tr:hover { background: #f8fafc; }
        .clt-inner-td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; font-size: 13px; font-weight: 600; color: #374151; vertical-align: middle; }
        .clt-inner-td-nested { padding: 12px; background: #f1f5f9; }
        .clt-nowrap { white-space: nowrap; }
        .clt-upper { text-transform: uppercase; }
        .clt-gw-name { display: flex; align-items: center; gap: 8px; }

        /* KYC status */
        .clt-kyc-status { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 700; text-transform: uppercase; }
        .clt-kyc-status--verified { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .clt-kyc-status--unverified { background: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }

        /* KYC actions */
        .clt-kyc-actions { display: flex; align-items: center; gap: 6px; }
        .clt-kyc-action-btn { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: 1.5px solid; transition: all .15s; background: none; }
        .clt-kyc-action-btn--upload { border-color: #bfdbfe; color: #2563eb; background: #eff6ff; }
        .clt-kyc-action-btn--upload:hover { background: #dbeafe; }
        .clt-kyc-action-btn--done { border-color: #bbf7d0; color: #16a34a; background: #f0fdf4; cursor: default; }
        .clt-kyc-action-btn--view { border-color: #bfdbfe; color: #2563eb; background: #eff6ff; }
        .clt-kyc-action-btn--view:hover { background: #dbeafe; }
        .clt-kyc-spinner { width: 20px; height: 20px; border: 2px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: cltSpin .7s linear infinite; }

        /* Payment method type badge */
        .clt-pm-type-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 5px; font-size: 11.5px; font-weight: 700; text-transform: capitalize; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1px solid #bfdbfe; color: #1d4ed8; }

        /* Empty */
        .clt-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 24px; gap: 10px; }
        .clt-empty-icon { width: 52px; height: 52px; border-radius: 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .clt-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .clt-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .clt-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px; gap: 12px; }
        .clt-spinner { width: 28px; height: 28px; border: 3px solid #e2e8f0; border-top-color: #16a34a; border-radius: 50%; animation: cltSpin .7s linear infinite; }
        @keyframes cltSpin { to{transform:rotate(360deg)} }
        .clt-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .clt-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .clt-header { flex-direction: column; align-items: flex-start; }
          .clt-search-wrap { width: 100%; }
          .clt-thead-th, .clt-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default CustomerTable;